import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { 
  Plus, Search, LogOut, 
  MoreVertical, Trash2, Edit, FolderOpen, X, 
  Lightbulb, User, Brain, BookOpen, Settings,
  PenTool, Video, BarChart3, MapPin, ClipboardPaste,
  ChevronRight, Upload, FileVideo, FileAudio, FileText, Loader2, Copy, Archive, ListMusic,
  Sparkles
} from "lucide-react";
import { toast } from "sonner";
import { API } from "@/App";
import BitWorkshop from "@/components/BitWorkshop";
import CategoryManager from "@/components/CategoryManager";
import IdeaVault from "@/components/IdeaVault";
import AIBrainstorm from "@/components/AIBrainstorm";
import ResourceLibrary from "@/components/ResourceLibrary";
import ProfileTab from "@/components/ProfileTab";
import SetReview from "@/components/SetReview";
import SetlistPlanner from "@/components/SetlistPlanner";
import PlayHome from "@/components/PlayHome";
import IdeaDetail from "@/components/IdeaDetail";

// Status badge colors
const STATUS_COLORS = {
  working: "bg-blue-500/20 text-blue-400",
  "stage-ready": "bg-green-500/20 text-green-400",
  retired: "bg-muted text-muted-foreground"
};

export default function Dashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user || null);
  
  // View mode: "zen" (minimal capture) or "full" (5-tab app)
  const [viewMode, setViewMode] = useState("zen");
  
  // Main navigation - 5 tabs (only visible in full mode)
  const [activeTab, setActiveTab] = useState("capture");
  
  // Global state
  const [isLoading, setIsLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  
  // Bits/Write state
  const [bits, setBits] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [editingBit, setEditingBit] = useState(null);
  const [isWorkshopOpen, setIsWorkshopOpen] = useState(false);
  const [isCategoryManagerOpen, setIsCategoryManagerOpen] = useState(false);
  const [initialContent, setInitialContent] = useState("");
  const [initialIdea, setInitialIdea] = useState("");
  const [isAIBrainstormOpen, setIsAIBrainstormOpen] = useState(false);
  const [brainstormBit, setBrainstormBit] = useState(null);
  
  // Header dialogs
  const [isResourceLibraryOpen, setIsResourceLibraryOpen] = useState(false);
  const [isIdeaVaultOpen, setIsIdeaVaultOpen] = useState(false);

  // Capture state
  const [ideas, setIdeas] = useState([]);
  const [newIdeaText, setNewIdeaText] = useState("");
  const [isSavingIdea, setIsSavingIdea] = useState(false);
  const [similarBits, setSimilarBits] = useState([]);
  const [isSearchingSimilar, setIsSearchingSimilar] = useState(false);
  const [pendingIdea, setPendingIdea] = useState(null);

  // Perform state
  const [performances, setPerformances] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isSavingTranscript, setIsSavingTranscript] = useState(false);
  const [performInputMode, setPerformInputMode] = useState("upload"); // "upload" or "paste"
  const [pastedTranscript, setPastedTranscript] = useState("");
  const [newPerformance, setNewPerformance] = useState({
    title: "",
    venue: "",
    date: new Date().toISOString().split('T')[0],
    audience_type: "",
    notes: "",
    video_url: ""
  });

  // Review state
  const [selectedPerformance, setSelectedPerformance] = useState(null);

  // Setlist Planner state
  const [setlists, setSetlists] = useState([]);
  const [selectedSetlistId, setSelectedSetlistId] = useState(null);
  const [performStatusFilter, setPerformStatusFilter] = useState(null);
  const [isNewSetlistDialogOpen, setIsNewSetlistDialogOpen] = useState(false);
  const [newSetlistName, setNewSetlistName] = useState("");

  // Play state
  const [playIdeas, setPlayIdeas] = useState([]);
  const [selectedPlayIdea, setSelectedPlayIdea] = useState(null);
  const [isSendingToPlay, setIsSendingToPlay] = useState(null);

  // Handle tab change - refresh data when switching to Play tab
  const handleTabChange = async (newTab) => {
    // If switching TO the Play tab, refresh the ideas list
    if (newTab === "play" && activeTab !== "play") {
      try {
        const res = await fetch(`${API}/play/ideas`, { credentials: 'include' });
        if (res.ok) {
          const freshIdeas = await res.json();
          setPlayIdeas(freshIdeas);
          // If there was a selected idea, refresh it too
          if (selectedPlayIdea) {
            const freshSelected = freshIdeas.find(i => i.idea_id === selectedPlayIdea.idea_id);
            if (freshSelected) {
              setSelectedPlayIdea(freshSelected);
            }
          }
        }
      } catch (error) {
        console.error("Failed to refresh play ideas:", error);
      }
    }
    setActiveTab(newTab);
  };

  // Fetch user
  useEffect(() => {
    const fetchUser = async () => {
      if (user) return;
      try {
        const response = await fetch(`${API}/auth/me`, { credentials: 'include' });
        if (response.ok) {
          setUser(await response.json());
        } else {
          navigate('/');
        }
      } catch (error) {
        navigate('/');
      }
    };
    fetchUser();
  }, [user, navigate]);

  // Fetch all data
  useEffect(() => {
    if (!user) return;
    
    const fetchData = async () => {
      try {
        const [bitsRes, categoriesRes, ideasRes, performancesRes, setlistsRes, playIdeasRes] = await Promise.all([
          fetch(`${API}/bits`, { credentials: 'include' }),
          fetch(`${API}/categories`, { credentials: 'include' }),
          fetch(`${API}/ideas`, { credentials: 'include' }),
          fetch(`${API}/transcriptions`, { credentials: 'include' }),
          fetch(`${API}/setlists`, { credentials: 'include' }),
          fetch(`${API}/play/ideas`, { credentials: 'include' })
        ]);

        if (bitsRes.ok) setBits(await bitsRes.json());
        if (categoriesRes.ok) setCategories(await categoriesRes.json());
        if (ideasRes.ok) setIdeas(await ideasRes.json());
        if (performancesRes.ok) setPerformances(await performancesRes.json());
        if (setlistsRes.ok) {
          const setlistData = await setlistsRes.json();
          setSetlists(setlistData);
          if (setlistData.length > 0 && !selectedSetlistId) {
            setSelectedSetlistId(setlistData[0].setlist_id);
          }
        }
        if (playIdeasRes.ok) setPlayIdeas(await playIdeasRes.json());
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [user]);

  const handleLogout = async () => {
    try {
      await fetch(`${API}/auth/logout`, { method: 'POST', credentials: 'include' });
      navigate('/');
    } catch (error) {
      toast.error("Logout failed");
    }
  };

  // ============ SETLIST FUNCTIONS ============
  const handleDeleteSetlist = async (setlistId) => {
    if (!confirm("Delete this setlist?")) return;
    
    try {
      const response = await fetch(`${API}/setlists/${setlistId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (response.ok) {
        setSetlists(setlists.filter(s => s.setlist_id !== setlistId));
        if (selectedSetlistId === setlistId) {
          const remaining = setlists.filter(s => s.setlist_id !== setlistId);
          setSelectedSetlistId(remaining[0]?.setlist_id || null);
        }
        toast.success("Setlist deleted");
      }
    } catch (error) {
      toast.error("Failed to delete setlist");
    }
  };

  const handleCreateSetlist = async () => {
    if (!newSetlistName.trim()) return;
    
    try {
      const response = await fetch(`${API}/setlists`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name: newSetlistName.trim() })
      });
      
      if (response.ok) {
        const newSetlist = await response.json();
        setSetlists([newSetlist, ...setlists]);
        setSelectedSetlistId(newSetlist.setlist_id);
        setNewSetlistName("");
        setIsNewSetlistDialogOpen(false);
        toast.success("Setlist created!");
      }
    } catch (error) {
      toast.error("Failed to create setlist");
    }
  };

  // ============ CAPTURE FUNCTIONS ============
  const handleSaveIdea = async () => {
    if (!newIdeaText.trim()) return;
    setIsSavingIdea(true);
    
    try {
      const response = await fetch(`${API}/ideas`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ content: newIdeaText.trim(), source: "text" })
      });
      
      if (response.ok) {
        const data = await response.json();
        setIdeas([data, ...ideas]);
        setNewIdeaText("");
        toast.success("Idea captured! 💡");
      }
    } catch (error) {
      toast.error("Failed to save idea");
    } finally {
      setIsSavingIdea(false);
    }
  };

  const handleWriteFromIdea = async (idea) => {
    setPendingIdea(idea);
    setIsSearchingSimilar(true);
    setSimilarBits([]);
    
    try {
      const response = await fetch(`${API}/ideas/find-similar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ content: idea.content })
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.similar_bits && data.similar_bits.length > 0) {
          setSimilarBits(data.similar_bits);
        } else {
          handleConvertToBit(idea);
          setPendingIdea(null);
        }
      } else {
        handleConvertToBit(idea);
        setPendingIdea(null);
      }
    } catch (error) {
      handleConvertToBit(idea);
      setPendingIdea(null);
    } finally {
      setIsSearchingSimilar(false);
    }
  };

  const handleLinkIdeaToBit = async (ideaId, bitId) => {
    try {
      const response = await fetch(`${API}/ideas/${ideaId}/link/${bitId}`, {
        method: 'POST',
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        if (data.bit) {
          setBits(bits.map(b => b.bit_id === bitId ? data.bit : b));
        }
        const linkedBit = data.bit || bits.find(b => b.bit_id === bitId);
        setIdeas(ideas.map(i => 
          i.idea_id === ideaId 
            ? { ...i, converted_to_bit: true, bit_id: bitId, linked_bit: linkedBit }
            : i
        ));
        setSimilarBits([]);
        setPendingIdea(null);
        toast.success("Idea added to bit! 💡");
      }
    } catch (error) {
      toast.error("Failed to link idea");
    }
  };

  const handleDismissSimilar = () => {
    if (pendingIdea) {
      handleConvertToBit(pendingIdea);
    }
    setSimilarBits([]);
    setPendingIdea(null);
  };

  const handleDeleteIdea = async (ideaId) => {
    try {
      const response = await fetch(`${API}/ideas/${ideaId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (response.ok) {
        setIdeas(ideas.filter(i => i.idea_id !== ideaId));
        toast.success("Idea deleted");
      }
    } catch (error) {
      toast.error("Failed to delete idea");
    }
  };

  // ============ PLAY FUNCTIONS ============
  const handleSendToPlay = async (idea) => {
    setIsSendingToPlay(idea.idea_id);
    try {
      const response = await fetch(`${API}/play/ideas/from-capture/${idea.idea_id}`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (response.ok) {
        const newPlayIdea = await response.json();
        setPlayIdeas([newPlayIdea, ...playIdeas]);
        setSelectedPlayIdea(newPlayIdea);
        
        // Remove the idea from Capture (same as Write behavior)
        await fetch(`${API}/ideas/${idea.idea_id}`, {
          method: 'DELETE',
          credentials: 'include'
        });
        setIdeas(ideas.filter(i => i.idea_id !== idea.idea_id));
        
        setActiveTab("play");
        toast.success("Idea sent to Play!");
      } else {
        toast.error("Failed to send to Play");
      }
    } catch (error) {
      toast.error("Failed to send to Play");
    } finally {
      setIsSendingToPlay(null);
    }
  };

  const handleOpenPlayIdea = async (idea) => {
    // Always fetch fresh data from server when opening an idea
    try {
      const response = await fetch(`${API}/play/ideas/${idea.idea_id}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const freshIdea = await response.json();
        setSelectedPlayIdea(freshIdea);
        // Also update the cached list
        setPlayIdeas(playIdeas.map(i => 
          i.idea_id === freshIdea.idea_id ? freshIdea : i
        ));
      } else {
        // Fallback to cached version if fetch fails
        setSelectedPlayIdea(idea);
      }
    } catch (error) {
      console.error("Failed to fetch idea:", error);
      setSelectedPlayIdea(idea);
    }
  };

  const handleBackToPlayHome = async () => {
    setSelectedPlayIdea(null);
    // Refresh the play ideas list to show any updates
    try {
      const res = await fetch(`${API}/play/ideas`, { credentials: 'include' });
      if (res.ok) {
        setPlayIdeas(await res.json());
      }
    } catch (error) {
      console.error("Failed to refresh play ideas:", error);
    }
  };

  const handlePlayBitCreated = (bit) => {
    // Add the new bit to the bits list and optionally navigate to Write
    if (bit) {
      setBits([bit, ...bits]);
    }
  };

  // Helper to generate unique title (checks for duplicates)
  const generateUniqueTitle = (baseTitle, existingBits) => {
    const titles = existingBits.map(b => b.title?.toLowerCase() || '');
    
    if (!titles.includes(baseTitle.toLowerCase())) {
      return baseTitle;
    }
    
    // Find a unique suffix
    let counter = 2;
    let newTitle = `${baseTitle} (${counter})`;
    while (titles.includes(newTitle.toLowerCase())) {
      counter++;
      newTitle = `${baseTitle} (${counter})`;
    }
    return newTitle;
  };

  const handleConvertToBit = async (idea) => {
    // Generate title from first line
    const generateTitle = (content) => {
      if (!content) return 'Untitled';
      const plain = content.replace(/<[^>]*>/g, '').trim();
      const firstLine = plain.split('\n')[0].trim();
      return firstLine.length > 50 ? firstLine.substring(0, 47) + '...' : firstLine || 'Untitled';
    };
    
    const baseTitle = generateTitle(idea.content);
    const title = generateUniqueTitle(baseTitle, bits); // Ensure unique title
    
    // Create the bit data with auto-generated section
    const bitData = {
      title: title,
      content: "", // Content lives in sections now
      sections: [{
        section_id: `sec_${Date.now()}_main`,
        title: title,
        content: idea.content,
        order: 0,
        included: true
      }],
      original_snapshot: idea.content,
      status: "working",
      category: null,
      notes: "",
      original_ideas: [idea.content]
    };
    
    try {
      // Create the bit in the database
      const response = await fetch(`${API}/bits`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(bitData)
      });
      
      if (response.ok) {
        const newBit = await response.json();
        
        // Add to bits list
        setBits([newBit, ...bits]);
        
        // Archive the idea to vault (not delete) with link to bit
        await fetch(`${API}/ideas/${idea.idea_id}/archive?bit_id=${newBit.bit_id}`, {
          method: 'POST',
          credentials: 'include'
        });
        setIdeas(ideas.filter(i => i.idea_id !== idea.idea_id));
        
        // Open workshop with the saved bit
        setEditingBit(newBit);
        setInitialContent("");
        setInitialIdea("");
        setIsWorkshopOpen(true);
        setActiveTab("write");
        
        toast.success("Bit created!");
      } else {
        toast.error("Failed to create bit");
      }
    } catch (error) {
      console.error("Failed to convert idea to bit:", error);
      toast.error("Failed to create bit");
    }
  };

  // ============ WRITE FUNCTIONS ============
  const handleNewBit = (content = "") => {
    setInitialContent(content);
    setInitialIdea("");
    setEditingBit(null);
    setIsWorkshopOpen(true); // Open Workshop instead of old editor
  };

  const handleEditBit = (bit) => {
    setEditingBit(bit);
    setInitialContent("");
    setInitialIdea("");
    setIsWorkshopOpen(true); // Open Workshop instead of old editor
  };

  const handleSaveBit = async (bitData) => {
    try {
      const url = editingBit ? `${API}/bits/${editingBit.bit_id}` : `${API}/bits`;
      const method = editingBit ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(bitData)
      });

      if (response.ok) {
        const savedBit = await response.json();
        if (editingBit) {
          setBits(bits.map(b => b.bit_id === savedBit.bit_id ? savedBit : b));
        } else {
          setBits([savedBit, ...bits]);
        }
        setIsWorkshopOpen(false);
        toast.success(editingBit ? "Version saved!" : "Bit saved!");
      }
    } catch (error) {
      toast.error("Failed to save bit");
    }
  };

  // Create new bit (for autosave of new bits)
  const handleCreateNewBit = async (bitData) => {
    try {
      // Ensure unique title
      const uniqueTitle = generateUniqueTitle(bitData.title || 'Untitled', bits);
      
      const response = await fetch(`${API}/bits`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...bitData,
          title: uniqueTitle
        })
      });

      if (response.ok) {
        const newBit = await response.json();
        setBits([newBit, ...bits]);
        setEditingBit(newBit); // Update editingBit so autosave continues to work
        return newBit;
      }
      return null;
    } catch (error) {
      console.error("Failed to create new bit:", error);
      return null;
    }
  };

  // Duplicate bit handler - creates a new bit with same content
  const handleDuplicateBit = async (bitData) => {
    try {
      // Ensure unique title for duplicate
      const uniqueTitle = generateUniqueTitle(bitData.title || 'Untitled', bits);
      
      const response = await fetch(`${API}/bits`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...bitData,
          title: uniqueTitle
        })
      });

      if (response.ok) {
        const newBit = await response.json();
        setBits([newBit, ...bits]);
        setIsWorkshopOpen(false);
        toast.success("Bit duplicated!");
        
        // Open the new bit in workshop
        setTimeout(() => {
          setEditingBit(newBit);
          setInitialContent("");
          setInitialIdea("");
          setIsWorkshopOpen(true);
        }, 100);
      }
    } catch (error) {
      toast.error("Failed to duplicate bit");
    }
  };

  // Duplicate bit from card - creates a copy and opens it
  const handleDuplicateBitFromCard = async (bit) => {
    try {
      const duplicateData = {
        title: `${bit.title || 'Untitled'} (Copy)`,
        content: bit.content || "",
        sections: (bit.sections || []).map(s => ({
          ...s,
          section_id: `sec_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
        })),
        original_snapshot: bit.original_snapshot || null,
        status: "working",
        category: bit.category || null,
        notes: bit.notes || "",
        inline_notes: [...(bit.inline_notes || [])],
        original_ideas: [...(bit.original_ideas || [])]
      };

      const response = await fetch(`${API}/bits`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(duplicateData)
      });

      if (response.ok) {
        const newBit = await response.json();
        setBits([newBit, ...bits]);
        toast.success("Bit duplicated!");
        
        // Open the new bit in workshop
        setEditingBit(newBit);
        setInitialContent("");
        setInitialIdea("");
        setIsWorkshopOpen(true);
      }
    } catch (error) {
      toast.error("Failed to duplicate bit");
    }
  };

  // Auto-save handler (doesn't create version, just saves current state)
  const handleAutoSaveBit = async (bitId, bitData) => {
    try {
      const response = await fetch(`${API}/bits/${bitId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ ...bitData, create_version: false })
      });

      if (response.ok) {
        const savedBit = await response.json();
        setBits(bits.map(b => b.bit_id === savedBit.bit_id ? savedBit : b));
      }
    } catch (error) {
      console.error("Auto-save failed:", error);
    }
  };

  const handleDeleteBit = async (bitId) => {
    try {
      const response = await fetch(`${API}/bits/${bitId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (response.ok) {
        setBits(bits.filter(b => b.bit_id !== bitId));
        toast.success("Bit deleted");
      }
    } catch (error) {
      toast.error("Failed to delete bit");
    }
  };

  const handleOpenBrainstorm = (bit) => {
    setBrainstormBit(bit);
    setIsAIBrainstormOpen(true);
  };

  // Extract selection as new bit (from Workshop)
  const handleExtractSelection = (selectedText) => {
    // Close current workshop and open a new one with selected text
    setIsWorkshopOpen(false);
    setTimeout(() => {
      setEditingBit(null);
      setInitialContent(selectedText);
      setInitialIdea("");
      setIsWorkshopOpen(true);
    }, 100);
    toast.success("Selection extracted as new bit");
  };

  // Promote selection/section to standalone Bit
  const handlePromoteToBit = (content, title = "") => {
    // Close current workshop and open a new one with the promoted content
    setIsWorkshopOpen(false);
    setTimeout(() => {
      setEditingBit(null);
      setInitialContent(content);
      setInitialIdea("");
      setIsWorkshopOpen(true);
      if (title) {
        // The title will be set in the new workshop
        toast.success(`"${title}" promoted to new Bit`);
      } else {
        toast.success("Promoted to new Bit");
      }
    }, 100);
  };

  // ============ PERFORM FUNCTIONS ============
  const handlePerformanceUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 500 * 1024 * 1024) {
      toast.error("File too large. Maximum 500MB.");
      return;
    }

    setIsUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch(`${API}/transcribe`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        
        // Use title if provided, otherwise use original filename
        const displayFilename = newPerformance.title?.trim() || data.filename;
        
        const performanceData = {
          ...data,
          filename: displayFilename,
          video_url: newPerformance.video_url,
          venue: newPerformance.venue,
          performance_date: newPerformance.date,
          audience_type: newPerformance.audience_type,
          performance_notes: newPerformance.notes
        };
        
        // Update the transcription with title, video_url, and metadata
        await fetch(`${API}/transcriptions/${data.transcription_id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ 
            filename: displayFilename,
            video_url: newPerformance.video_url || null,
            notes: JSON.stringify({
              venue: newPerformance.venue,
              performance_date: newPerformance.date,
              audience_type: newPerformance.audience_type,
              performance_notes: newPerformance.notes,
              original_filename: data.filename
            })
          })
        });

        setPerformances([performanceData, ...performances]);
        setNewPerformance({ title: "", venue: "", date: new Date().toISOString().split('T')[0], audience_type: "", notes: "", video_url: "" });
        toast.success("Performance uploaded!");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Upload failed");
      }
    } catch (error) {
      toast.error("Upload failed");
    } finally {
      setIsUploading(false);
    }
  };

  const handleSaveTranscript = async () => {
    if (!pastedTranscript.trim()) {
      toast.error("Please paste your transcript");
      return;
    }

    setIsSavingTranscript(true);
    try {
      const response = await fetch(`${API}/transcribe/paste`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          transcript: pastedTranscript,
          title: newPerformance.title || null,
          venue: newPerformance.venue || null,
          performance_date: newPerformance.date || null,
          audience_type: newPerformance.audience_type || null,
          notes: newPerformance.notes || null,
          video_url: newPerformance.video_url || null
        })
      });

      if (response.ok) {
        const data = await response.json();
        setPerformances([data, ...performances]);
        setPastedTranscript("");
        setNewPerformance({ title: "", venue: "", date: new Date().toISOString().split('T')[0], audience_type: "", notes: "", video_url: "" });
        toast.success("Set logged!");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to save");
      }
    } catch (error) {
      toast.error("Failed to save transcript");
    } finally {
      setIsSavingTranscript(false);
    }
  };

  // ============ REVIEW FUNCTIONS ============
  const handleSelectPerformance = (performance) => {
    setSelectedPerformance(performance);
  };

  const handleDeletePerformance = async (e, performanceId) => {
    e.stopPropagation(); // Prevent triggering the select handler
    
    if (!window.confirm("Delete this performance? This cannot be undone.")) {
      return;
    }
    
    try {
      // Use POST to /delete endpoint as DELETE method may be blocked by proxy
      const response = await fetch(`${API}/transcriptions/${performanceId}/delete`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (response.ok) {
        setPerformances(performances.filter(p => p.transcription_id !== performanceId));
        if (selectedPerformance?.transcription_id === performanceId) {
          setSelectedPerformance(null);
        }
        toast.success("Performance deleted");
      } else {
        const error = await response.json().catch(() => ({}));
        toast.error(error.detail || "Failed to delete performance");
      }
    } catch (error) {
      toast.error("Failed to delete performance");
    }
  };

  // Filter bits
  const filteredBits = bits.filter(bit => {
    const matchesSearch = !searchQuery || 
      bit.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      bit.content?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = !selectedCategory || bit.category === selectedCategory;
    const matchesStatus = !selectedStatus || bit.status === selectedStatus;
    return matchesSearch && matchesCategory && matchesStatus;
  });

  const getCategoryColor = (categoryName) => {
    const cat = categories.find(c => c.name === categoryName);
    return cat?.color || '#FFFF00';
  };

  const parsePerformanceNotes = (notesStr) => {
    try {
      return JSON.parse(notesStr || '{}');
    } catch {
      return { performance_notes: notesStr };
    }
  };

  const getPreviewText = (content, maxLength = 150) => {
    if (!content) return "";
    const text = content.replace(/\n+/g, ' ').trim();
    return text.length > maxLength ? text.substring(0, maxLength) + "..." : text;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // ============ ZEN CAPTURE MODE ============
  // Minimal, distraction-free capture experience
  if (viewMode === "zen") {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        {/* Centered capture area with logo above */}
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="w-full max-w-2xl text-center">
            {/* Logo - prominent, right above input */}
            <img 
              src="https://customer-assets.emergentagent.com/job_laughcrafter/artifacts/c6aabxrp_ITM_Logo_1000px.png" 
              alt="BitStack" 
              className="h-20 md:h-24 w-auto mx-auto mb-8"
            />
            
            {/* Capture input - auto-focused */}
            <Textarea
              placeholder="What's funny right now?"
              value={newIdeaText}
              onChange={(e) => setNewIdeaText(e.target.value)}
              className="font-mono text-lg bg-transparent border-0 focus:ring-0 focus-visible:ring-0 focus-visible:outline-none resize-none min-h-[150px] text-center placeholder:text-white/70"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey && newIdeaText.trim()) {
                  e.preventDefault();
                  handleSaveIdea();
                }
              }}
            />
            
            {/* Capture button - only shows when there's text */}
            {newIdeaText.trim() && (
              <div className="flex justify-center mt-6">
                <Button 
                  onClick={handleSaveIdea}
                  disabled={isSavingIdea}
                  size="lg"
                  className="bg-primary text-primary-foreground px-8"
                >
                  {isSavingIdea ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <Lightbulb className="w-5 h-5 mr-2" />
                      Capture
                    </>
                  )}
                </Button>
              </div>
            )}
            
            {/* Open BitStack - positioned right below capture area */}
            <div className="mt-10">
              <button
                onClick={() => setViewMode("full")}
                className="inline-flex items-center gap-2 px-6 py-3 text-base font-medium text-white/80 hover:text-white bg-white/10 hover:bg-white/20 rounded-lg transition-all"
              >
                <span>Open Your BitStack</span>
                <ChevronRight className="w-5 h-5" />
              </button>
              <p className="text-xs text-white/50 mt-2">Write, organize & remix your material</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ============ FULL APP MODE ============
  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="border-b border-border p-3 md:p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button 
            onClick={() => setViewMode("zen")}
            className="flex items-center gap-3 hover:opacity-80 transition-opacity"
            title="Back to Zen Capture"
          >
            <img 
              src="https://customer-assets.emergentagent.com/job_laughcrafter/artifacts/c6aabxrp_ITM_Logo_1000px.png" 
              alt="BitStack" 
              className="h-8 md:h-10 w-auto"
            />
            <span className="font-display text-lg uppercase tracking-wider hidden sm:inline">BitStack</span>
          </button>
        </div>
        
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setViewMode("zen")}
            className="gap-2 text-muted-foreground"
            title="Zen Capture"
          >
            <Lightbulb className="w-4 h-4" />
            <span className="hidden sm:inline">Capture</span>
          </Button>
          
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsResourceLibraryOpen(true)}
            className="gap-2"
          >
            <BookOpen className="w-4 h-4" />
            <span className="hidden sm:inline">Resources</span>
          </Button>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2 hover:bg-muted/50 p-2 rounded-sm">
                <Avatar className="h-8 w-8">
                  <AvatarImage src={user?.picture} alt={user?.name} />
                  <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                    {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                  </AvatarFallback>
                </Avatar>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem onClick={() => handleTabChange("profile")}>
                <User className="w-4 h-4 mr-2" />
                Profile
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout}>
                <LogOut className="w-4 h-4 mr-2" />
                Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Main Tabs - 6 tab navigation */}
      <Tabs value={activeTab} onValueChange={handleTabChange} className="flex-1 flex flex-col">
        <div className="border-b border-border px-4">
          <TabsList className="h-12 bg-transparent gap-1 md:gap-2">
            <TabsTrigger value="capture" className="data-[state=active]:bg-primary/10 data-[state=active]:text-primary gap-1 md:gap-2 px-2 md:px-4" data-testid="tab-capture">
              <Lightbulb className="w-4 h-4" />
              <span className="hidden sm:inline">Capture</span>
            </TabsTrigger>
            <TabsTrigger value="play" className="data-[state=active]:bg-primary/10 data-[state=active]:text-primary gap-1 md:gap-2 px-2 md:px-4" data-testid="tab-play">
              <Sparkles className="w-4 h-4" />
              <span className="hidden sm:inline">Play</span>
            </TabsTrigger>
            <TabsTrigger value="write" className="data-[state=active]:bg-primary/10 data-[state=active]:text-primary gap-1 md:gap-2 px-2 md:px-4" data-testid="tab-write">
              <PenTool className="w-4 h-4" />
              <span className="hidden sm:inline">Write</span>
            </TabsTrigger>
            <TabsTrigger value="perform" className="data-[state=active]:bg-primary/10 data-[state=active]:text-primary gap-1 md:gap-2 px-2 md:px-4" data-testid="tab-perform">
              <Video className="w-4 h-4" />
              <span className="hidden sm:inline">Perform</span>
            </TabsTrigger>
            <TabsTrigger value="review" className="data-[state=active]:bg-primary/10 data-[state=active]:text-primary gap-1 md:gap-2 px-2 md:px-4" data-testid="tab-review">
              <BarChart3 className="w-4 h-4" />
              <span className="hidden sm:inline">Review</span>
            </TabsTrigger>
            <TabsTrigger value="profile" className="data-[state=active]:bg-primary/10 data-[state=active]:text-primary gap-1 md:gap-2 px-2 md:px-4" data-testid="tab-profile">
              <User className="w-4 h-4" />
              <span className="hidden sm:inline">Profile</span>
            </TabsTrigger>
          </TabsList>
        </div>

        {/* ============ CAPTURE TAB ============ */}
        <TabsContent value="capture" className="flex-1 p-4 md:p-6 mt-0">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="font-display text-2xl uppercase tracking-wider mb-2">Finding the Funny</h2>
              <p className="text-muted-foreground text-sm">
                No analysis, no judgment. Just capture the spark.
              </p>
            </div>

            {/* Quick Input */}
            <div className="bg-card border border-border rounded-sm p-4 mb-6">
              <Textarea
                placeholder="What's funny right now? Jot it down..."
                value={newIdeaText}
                onChange={(e) => setNewIdeaText(e.target.value)}
                className="font-mono text-sm bg-background/50 border-0 min-h-[100px] resize-none mb-3"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSaveIdea();
                  }
                }}
              />
              <div className="flex justify-between items-center">
                <span className="text-xs text-muted-foreground">Press Enter to save</span>
                <Button 
                  onClick={handleSaveIdea}
                  disabled={!newIdeaText.trim() || isSavingIdea}
                  className="bg-primary text-primary-foreground"
                >
                  {isSavingIdea ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Lightbulb className="w-4 h-4 mr-2" />
                  )}
                  Capture
                </Button>
              </div>
            </div>

            {/* Similar Bits Panel */}
            {(similarBits.length > 0 || isSearchingSimilar) && pendingIdea && (
              <div className="bg-primary/10 border border-primary/30 rounded-sm p-4 mb-6">
                {isSearchingSimilar ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="w-5 h-5 animate-spin text-primary mr-2" />
                    <span className="text-sm text-muted-foreground">Checking for similar bits...</span>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-display text-sm uppercase tracking-wider text-primary">
                        Similar Material Found
                      </h4>
                      <Button variant="ghost" size="sm" onClick={handleDismissSimilar} className="h-6 px-2 text-xs">
                        Skip & Write New
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground mb-3">
                      This idea might fit with material you have already written:
                    </p>
                    <div className="space-y-2">
                      {similarBits.map((bit) => (
                        <div key={bit.bit_id} className="bg-background/50 rounded-sm p-3 border border-border">
                          <p className="font-semibold text-sm mb-1">{bit.title || "Untitled"}</p>
                          <p className="font-mono text-xs text-muted-foreground line-clamp-2 mb-2">
                            {getPreviewText(bit.content, 100)}
                          </p>
                          <p className="text-xs text-primary mb-2 italic">{bit.match_reason}</p>
                          <div className="flex gap-2">
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={async () => { 
                                // Archive the idea to vault with link to this bit
                                try {
                                  await fetch(`${API}/ideas/${pendingIdea.idea_id}/archive?bit_id=${bit.bit_id}`, {
                                    method: 'POST',
                                    credentials: 'include'
                                  });
                                  setIdeas(ideas.filter(i => i.idea_id !== pendingIdea.idea_id));
                                } catch (error) {
                                  console.error("Failed to archive idea:", error);
                                }
                                setEditingBit(bit);
                                setInitialIdea(pendingIdea?.content || "");
                                setInitialContent("");
                                setIsWorkshopOpen(true); 
                                setActiveTab("write");
                                setSimilarBits([]);
                                setPendingIdea(null);
                              }}
                              className="text-xs h-7"
                            >
                              View & Edit
                            </Button>
                            <Button 
                              size="sm"
                              onClick={async () => {
                                // Add the idea as a new section to the existing bit
                                const ideaContent = pendingIdea?.content || "";
                                const generateTitle = (content) => {
                                  if (!content) return 'New Idea';
                                  const plain = content.replace(/<[^>]*>/g, '').trim();
                                  const firstLine = plain.split('\n')[0].trim();
                                  return firstLine.length > 50 ? firstLine.substring(0, 47) + '...' : firstLine || 'New Idea';
                                };
                                
                                // Create new section from the idea
                                const newSection = {
                                  section_id: `sec_${Date.now()}_idea`,
                                  title: generateTitle(ideaContent),
                                  content: ideaContent,
                                  order: (bit.sections?.length || 0),
                                  included: true
                                };
                                
                                // Update the bit with the new section
                                const updatedSections = [...(bit.sections || []), newSection];
                                const updatedOriginalIdeas = [...(bit.original_ideas || []), ideaContent];
                                
                                try {
                                  const response = await fetch(`${API}/bits/${bit.bit_id}`, {
                                    method: 'PUT',
                                    headers: { 'Content-Type': 'application/json' },
                                    credentials: 'include',
                                    body: JSON.stringify({
                                      sections: updatedSections,
                                      original_ideas: updatedOriginalIdeas
                                    })
                                  });
                                  
                                  if (response.ok) {
                                    const updatedBit = await response.json();
                                    
                                    // Update bits list
                                    setBits(bits.map(b => b.bit_id === updatedBit.bit_id ? updatedBit : b));
                                    
                                    // Archive the idea to vault with link to this bit
                                    await fetch(`${API}/ideas/${pendingIdea.idea_id}/archive?bit_id=${bit.bit_id}`, {
                                      method: 'POST',
                                      credentials: 'include'
                                    });
                                    setIdeas(ideas.filter(i => i.idea_id !== pendingIdea.idea_id));
                                    
                                    // Open the updated bit in workshop
                                    setEditingBit(updatedBit);
                                    setInitialIdea("");
                                    setInitialContent("");
                                    setIsWorkshopOpen(true); 
                                    setActiveTab("write");
                                    setSimilarBits([]);
                                    setPendingIdea(null);
                                    toast.success("Idea added as new section!");
                                  } else {
                                    toast.error("Failed to add idea to bit");
                                  }
                                } catch (error) {
                                  console.error("Failed to add idea to bit:", error);
                                  toast.error("Failed to add idea to bit");
                                }
                              }}
                              className="text-xs h-7 bg-primary text-primary-foreground"
                            >
                              Add to This Bit
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Ideas List */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                  Ideas ({ideas.length})
                </h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsIdeaVaultOpen(true)}
                  className="text-xs gap-1 text-muted-foreground hover:text-foreground"
                >
                  <Archive className="w-3 h-3" />
                  Idea Vault
                </Button>
              </div>
              
              <ScrollArea className="h-[400px]">
                <div className="space-y-2 pr-4">
                  {ideas.length === 0 ? (
                    <div className="text-center py-12">
                      <Lightbulb className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                      <p className="text-muted-foreground">Start capturing</p>
                    </div>
                  ) : (
                    ideas.map((idea) => (
                      <div 
                        key={idea.idea_id}
                        className={`p-3 rounded-sm group transition-colors ${
                          idea.converted_to_bit 
                            ? 'bg-primary/5 border border-primary/20' 
                            : 'bg-muted/50 hover:bg-muted/80'
                        }`}
                      >
                        <p className={`font-mono text-sm ${idea.converted_to_bit ? 'text-muted-foreground' : ''}`}>
                          {idea.content}
                        </p>
                        
                        {/* Converted/Linked Status */}
                        {idea.converted_to_bit && idea.linked_bit && (
                          <div className="mt-2 p-2 bg-background/50 rounded-sm border border-border">
                            <div className="flex items-center gap-2 text-xs text-primary mb-1">
                              <PenTool className="w-3 h-3" />
                              <span>Written into bit</span>
                            </div>
                            <p className="font-mono text-xs text-muted-foreground line-clamp-1">
                              {idea.linked_bit.title || getPreviewText(idea.linked_bit.content, 50)}
                            </p>
                            <div className="flex items-center justify-between mt-2">
                              <Button 
                                size="sm" 
                                variant="ghost"
                                className="h-6 px-2 text-xs"
                                onClick={() => { 
                                  setEditingBit(idea.linked_bit); 
                                  setInitialContent("");
                                  setInitialIdea("");
                                  setIsWorkshopOpen(true); 
                                  setActiveTab("write"); 
                                }}
                              >
                                View Bit
                              </Button>
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive opacity-0 group-hover:opacity-100 transition-opacity"
                                onClick={() => handleDeleteIdea(idea.idea_id)}
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          </div>
                        )}
                        
                        {/* Not converted yet */}
                        {!idea.converted_to_bit && (
                          <div className="flex items-center justify-between mt-2">
                            <span className="text-xs text-muted-foreground">
                              {new Date(idea.created_at).toLocaleDateString()}
                            </span>
                            <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                className="h-7 px-2 text-xs"
                                onClick={() => handleSendToPlay(idea)}
                                disabled={isSendingToPlay === idea.idea_id}
                                title="Mess with this idea and see what it turns into"
                                data-testid={`play-idea-${idea.idea_id}`}
                              >
                                {isSendingToPlay === idea.idea_id ? (
                                  <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                ) : (
                                  <Sparkles className="w-3 h-3 mr-1" />
                                )}
                                Play
                              </Button>
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                className="h-7 px-2 text-xs text-primary"
                                onClick={() => handleWriteFromIdea(idea)}
                                disabled={isSearchingSimilar}
                                title="Start shaping this into a bit"
                                data-testid={`write-idea-${idea.idea_id}`}
                              >
                                {isSearchingSimilar && pendingIdea?.idea_id === idea.idea_id ? (
                                  <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                ) : (
                                  <ChevronRight className="w-3 h-3 mr-1" />
                                )}
                                Write
                              </Button>
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
                                onClick={() => handleDeleteIdea(idea.idea_id)}
                                data-testid={`delete-idea-${idea.idea_id}`}
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </ScrollArea>
            </div>
          </div>
        </TabsContent>

        {/* ============ PLAY TAB ============ */}
        <TabsContent value="play" className="flex-1 mt-0 overflow-hidden min-h-0 flex flex-col">
          {selectedPlayIdea ? (
            <IdeaDetail
              idea={selectedPlayIdea}
              onBack={handleBackToPlayHome}
              onUpdate={(updatedIdea) => {
                setPlayIdeas(playIdeas.map(i => 
                  i.idea_id === updatedIdea.idea_id ? updatedIdea : i
                ));
                setSelectedPlayIdea(updatedIdea);
              }}
              bits={bits}
              onBitCreated={handlePlayBitCreated}
            />
          ) : (
            <PlayHome
              playIdeas={playIdeas}
              setPlayIdeas={setPlayIdeas}
              onOpenIdea={handleOpenPlayIdea}
              onRefresh={async () => {
                const res = await fetch(`${API}/play/ideas`, { credentials: 'include' });
                if (res.ok) setPlayIdeas(await res.json());
              }}
            />
          )}
        </TabsContent>

        {/* ============ WRITE TAB ============ */}
        <TabsContent value="write" className="flex-1 mt-0 flex">
          {/* Sidebar */}
          <aside className="hidden md:flex w-56 border-r border-border bg-card flex-col p-4">
            {/* Status Filter */}
            <div className="mb-6">
              <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground block mb-3">Status</span>
              <div className="space-y-1">
                {[
                  { value: null, label: "All Bits" },
                  { value: "working", label: "Working" },
                  { value: "stage-ready", label: "Stage Ready" },
                  { value: "retired", label: "Retired" }
                ].map((status) => (
                  <button
                    key={status.value || 'all'}
                    onClick={() => setSelectedStatus(status.value)}
                    className={`w-full text-left px-3 py-2 rounded-sm text-sm ${selectedStatus === status.value ? 'bg-muted' : 'hover:bg-muted/50'}`}
                  >
                    {status.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Categories */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Categories</span>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="h-6 w-6 p-0"
                  onClick={() => setIsCategoryManagerOpen(true)}
                >
                  <FolderOpen className="w-3 h-3" />
                </Button>
              </div>
              <ScrollArea className="h-32">
                <div className="space-y-1">
                  <button
                    onClick={() => setSelectedCategory(null)}
                    className={`w-full text-left px-3 py-2 rounded-sm text-sm ${!selectedCategory ? 'bg-muted' : 'hover:bg-muted/50'}`}
                  >
                    All Categories
                  </button>
                  {categories.map((cat) => (
                    <button
                      key={cat.category_id}
                      onClick={() => setSelectedCategory(cat.name)}
                      className={`w-full text-left px-3 py-2 rounded-sm text-sm flex items-center gap-2 ${selectedCategory === cat.name ? 'bg-muted' : 'hover:bg-muted/50'}`}
                    >
                      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: cat.color }} />
                      {cat.name}
                    </button>
                  ))}
                </div>
              </ScrollArea>
            </div>
          </aside>

          {/* Bits Area */}
          <div className="flex-1 flex flex-col">
            {/* Toolbar */}
            <div className="border-b border-border p-3 flex items-center gap-3">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search bits..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 bg-muted border-0 text-sm"
                />
              </div>
              {(selectedCategory || selectedStatus || searchQuery) && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => { setSelectedCategory(null); setSelectedStatus(null); setSearchQuery(""); }}
                >
                  <X className="w-4 h-4 mr-1" />
                  Clear
                </Button>
              )}
              <Button onClick={() => handleNewBit()} className="bg-primary text-primary-foreground">
                <Plus className="w-4 h-4 mr-2" />
                New Bit
              </Button>
            </div>

            {/* Bits Grid */}
            <div className="flex-1 p-4 overflow-auto">
              {filteredBits.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center">
                  <PenTool className="w-12 h-12 text-muted-foreground mb-4" />
                  <h3 className="font-display text-xl uppercase mb-2">No Bits Yet</h3>
                  <p className="text-muted-foreground text-sm mb-4">
                    {searchQuery || selectedCategory || selectedStatus
                      ? "No bits match your filters."
                      : "Start writing your material!"}
                  </p>
                </div>
              ) : (
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {filteredBits.map((bit) => (
                    <div
                      key={bit.bit_id}
                      className="group p-4 bg-card border border-border rounded-sm hover:border-primary/50 transition-all cursor-pointer"
                      onClick={() => handleEditBit(bit)}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge className={`text-xs ${STATUS_COLORS[bit.status] || STATUS_COLORS.draft}`}>
                            {bit.status}
                          </Badge>
                          {bit.category && (
                            <span 
                              className="w-2 h-2 rounded-full"
                              style={{ backgroundColor: getCategoryColor(bit.category) }}
                            />
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                              <button className="p-1 rounded text-muted-foreground hover:text-foreground">
                                <MoreVertical className="w-4 h-4" />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleEditBit(bit); }}>
                                <Edit className="w-4 h-4 mr-2" />
                                Edit
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleDuplicateBitFromCard(bit); }}>
                                <Copy className="w-4 h-4 mr-2" />
                                Duplicate
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleOpenBrainstorm(bit); }}>
                                <Brain className="w-4 h-4 mr-2" />
                                AI Brainstorm
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem 
                                onClick={(e) => { e.stopPropagation(); handleDeleteBit(bit.bit_id); }}
                                className="text-destructive"
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>

                      <h4 className="font-semibold text-sm mb-1">{bit.title || "Untitled"}</h4>
                      <p className="font-mono text-xs text-muted-foreground line-clamp-3">
                        {getPreviewText(bit.content, 120)}
                      </p>

                      {bit.tags?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-3">
                          {bit.tags.slice(0, 3).map((tag) => (
                            <Badge key={tag} variant="secondary" className="text-xs">
                              {tag}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        {/* ============ PERFORM TAB (Setlist Planner) ============ */}
        <TabsContent value="perform" className="flex-1 mt-0 flex overflow-hidden items-start">
          {/* Sidebar */}
          <aside className="hidden md:flex w-56 border-r border-border bg-card flex-col p-4 overflow-auto h-full">
            {/* Status Filter */}
            <div className="mb-6">
              <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground block mb-3">
                Filter Bits
              </span>
              <div className="space-y-1">
                {[
                  { value: null, label: "All Bits" },
                  { value: "working", label: "Working" },
                  { value: "stage-ready", label: "Stage Ready" },
                  { value: "retired", label: "Retired" }
                ].map((status) => (
                  <button
                    key={status.value || 'all'}
                    onClick={() => setPerformStatusFilter(status.value)}
                    className={`w-full text-left px-3 py-2 rounded-sm text-sm ${performStatusFilter === status.value ? 'bg-muted' : 'hover:bg-muted/50'}`}
                  >
                    {status.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Setlists */}
            <div className="flex-1 flex flex-col min-h-0">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Setlists
                </span>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="h-6 w-6 p-0"
                  onClick={() => setIsNewSetlistDialogOpen(true)}
                >
                  <Plus className="w-3 h-3" />
                </Button>
              </div>
              <ScrollArea className="flex-1">
                <div className="space-y-1 pr-2">
                  {setlists.length === 0 ? (
                    <p className="text-xs text-muted-foreground text-center py-4">
                      No setlists yet
                    </p>
                  ) : (
                    setlists.map((setlist) => (
                      <div
                        key={setlist.setlist_id}
                        className={`group flex items-center gap-2 px-3 py-2 rounded-sm text-sm cursor-pointer transition-all ${
                          selectedSetlistId === setlist.setlist_id 
                            ? 'bg-primary text-primary-foreground font-medium' 
                            : 'hover:bg-muted/50'
                        }`}
                        onClick={() => setSelectedSetlistId(setlist.setlist_id)}
                      >
                        <ListMusic className={`w-4 h-4 shrink-0 ${selectedSetlistId === setlist.setlist_id ? 'text-primary-foreground' : 'text-primary'}`} />
                        <span className="flex-1 truncate">{setlist.name}</span>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDeleteSetlist(setlist.setlist_id); }}
                          className={`p-1 opacity-0 group-hover:opacity-100 ${selectedSetlistId === setlist.setlist_id ? 'text-primary-foreground/70 hover:text-primary-foreground' : 'text-muted-foreground hover:text-destructive'}`}
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </ScrollArea>
            </div>
          </aside>

          {/* Main Content */}
          <SetlistPlanner 
            bits={bits} 
            setlists={setlists}
            setSetlists={setSetlists}
            selectedSetlistId={selectedSetlistId}
            setSelectedSetlistId={setSelectedSetlistId}
            statusFilter={performStatusFilter}
            onDeleteSetlist={handleDeleteSetlist}
            onEditBit={(bit) => {
              // Navigate to Write tab and open the bit in workshop
              setEditingBit(bit);
              setInitialContent("");
              setInitialIdea("");
              setIsWorkshopOpen(true);
              setActiveTab("write");
            }}
          />
        </TabsContent>

        {/* ============ REVIEW TAB (Merged: Log Sets + Review Performance) ============ */}
        <TabsContent value="review" className="flex-1 mt-0 overflow-hidden">
          {selectedPerformance ? (
            <div className="h-full">
              <SetReview
              performance={selectedPerformance}
              onClose={() => setSelectedPerformance(null)}
              bits={bits}
              onBitCreated={async () => {
                // Refresh bits and performances
                const [bitsRes, performancesRes] = await Promise.all([
                  fetch(`${API}/bits`, { credentials: 'include' }),
                  fetch(`${API}/transcriptions`, { credentials: 'include' })
                ]);
                if (bitsRes.ok) setBits(await bitsRes.json());
                if (performancesRes.ok) {
                  const newPerformances = await performancesRes.json();
                  setPerformances(newPerformances);
                  // Update selected performance with fresh data
                  const updated = newPerformances.find(p => p.transcription_id === selectedPerformance.transcription_id);
                  if (updated) setSelectedPerformance(updated);
                }
              }}
              onSwitchToWrite={async (bitId) => {
                // Fetch fresh bit data before opening
                try {
                  const response = await fetch(`${API}/bits/${bitId}`, { credentials: 'include' });
                  if (response.ok) {
                    const freshBit = await response.json();
                    setEditingBit(freshBit);
                    setIsWorkshopOpen(true);
                    setActiveTab("write");
                  } else {
                    // Fallback to cached version
                    const bit = bits.find(b => b.bit_id === bitId);
                    if (bit) {
                      setEditingBit(bit);
                      setIsWorkshopOpen(true);
                      setActiveTab("write");
                    }
                  }
                } catch (error) {
                  // Fallback to cached version
                  const bit = bits.find(b => b.bit_id === bitId);
                  if (bit) {
                    setEditingBit(bit);
                    setIsWorkshopOpen(true);
                    setActiveTab("write");
                  }
                }
              }}
            />
            </div>
          ) : (
            <div className="p-4 md:p-6">
              <div className="max-w-4xl mx-auto">
                <div className="text-center mb-8">
                  <h2 className="font-display text-2xl uppercase tracking-wider mb-2">Review Your Sets</h2>
                  <p className="text-muted-foreground text-sm">
                    Log your performances and review what worked.
                  </p>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  {/* Upload/Paste Section */}
                  <div className="bg-card border border-border rounded-sm p-6">
                    <h3 className="font-display text-sm uppercase tracking-wider mb-4 text-muted-foreground">Log a Set</h3>
                    {/* Mode Toggle */}
                    <div className="flex gap-2 mb-4">
                      <Button
                        variant={performInputMode === "upload" ? "default" : "outline"}
                        size="sm"
                        onClick={() => setPerformInputMode("upload")}
                        className={performInputMode === "upload" ? "bg-primary text-primary-foreground" : ""}
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        Upload
                      </Button>
                      <Button
                        variant={performInputMode === "paste" ? "default" : "outline"}
                        size="sm"
                        onClick={() => setPerformInputMode("paste")}
                        className={performInputMode === "paste" ? "bg-primary text-primary-foreground" : ""}
                      >
                        <ClipboardPaste className="w-4 h-4 mr-2" />
                        Paste
                      </Button>
                    </div>

                    <div className="space-y-4">
                      {/* Title - for both upload and paste */}
                      <div>
                        <label className="text-sm text-muted-foreground mb-1 block">Title</label>
                        <Input
                          placeholder="Name this set"
                          value={newPerformance.title}
                          onChange={(e) => setNewPerformance({...newPerformance, title: e.target.value})}
                          className="bg-background/50"
                        />
                      </div>

                      <div>
                        <label className="text-sm text-muted-foreground mb-1 block">Venue</label>
                        <Input
                          placeholder="Comedy Club, Open Mic, etc."
                          value={newPerformance.venue}
                          onChange={(e) => setNewPerformance({...newPerformance, venue: e.target.value})}
                          className="bg-background/50"
                        />
                      </div>
                      <div>
                        <label className="text-sm text-muted-foreground mb-1 block">Date</label>
                        <Input
                          type="date"
                          value={newPerformance.date}
                          onChange={(e) => setNewPerformance({...newPerformance, date: e.target.value})}
                          className="bg-background/50"
                        />
                      </div>
                      <div>
                        <label className="text-sm text-muted-foreground mb-1 block">Audience</label>
                        <Select
                          value={newPerformance.audience_type}
                          onValueChange={(value) => setNewPerformance({...newPerformance, audience_type: value})}
                        >
                          <SelectTrigger className="bg-background/50">
                            <SelectValue placeholder="How was the crowd?" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="hot">🔥 Hot crowd</SelectItem>
                            <SelectItem value="warm">👍 Warm</SelectItem>
                            <SelectItem value="cold">🥶 Cold</SelectItem>
                            <SelectItem value="bombed">💣 Bombed</SelectItem>
                            <SelectItem value="mixed">🎭 Mixed</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="text-sm text-muted-foreground mb-1 block">Quick Notes</label>
                        <Textarea
                          placeholder="Initial thoughts..."
                          value={newPerformance.notes}
                          onChange={(e) => setNewPerformance({...newPerformance, notes: e.target.value})}
                          className="bg-background/50 min-h-[60px] resize-none"
                        />
                      </div>
                      <div>
                        <label className="text-sm text-muted-foreground mb-1 block">Video Link</label>
                        <Input
                          placeholder="https://youtube.com/... or any video URL"
                          value={newPerformance.video_url}
                          onChange={(e) => setNewPerformance({...newPerformance, video_url: e.target.value})}
                          className="bg-background/50"
                        />
                      </div>

                      {/* Paste Mode - Transcript Input */}
                      {performInputMode === "paste" && (
                        <div>
                          <label className="text-sm text-muted-foreground mb-1 block">Transcript</label>
                          <Textarea
                            placeholder="Paste your full set here..."
                            value={pastedTranscript}
                            onChange={(e) => setPastedTranscript(e.target.value)}
                            className="bg-background/50 min-h-[200px] resize-y font-mono text-sm"
                          />
                          <p className="text-xs text-muted-foreground mt-1">
                            {pastedTranscript.length > 0 && `${pastedTranscript.split(/\s+/).filter(Boolean).length} words`}
                          </p>
                        </div>
                      )}

                      {/* Upload Mode - File Input */}
                      {performInputMode === "upload" && (
                        <>
                          <div className="relative">
                            <input
                              type="file"
                              accept="audio/*,video/*"
                              onChange={handlePerformanceUpload}
                              disabled={isUploading}
                              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                            />
                            <Button 
                              className="w-full bg-primary text-primary-foreground"
                              disabled={isUploading}
                            >
                              {isUploading ? (
                                <>
                                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                  Uploading...
                                </>
                              ) : (
                                <>
                                  <Upload className="w-4 h-4 mr-2" />
                                  Upload Recording
                                </>
                              )}
                            </Button>
                          </div>
                          <p className="text-xs text-muted-foreground text-center">
                            Supports audio & video up to 500MB
                          </p>
                        </>
                      )}

                      {/* Paste Mode - Save Button */}
                      {performInputMode === "paste" && (
                        <Button 
                          className="w-full bg-primary text-primary-foreground"
                          onClick={handleSaveTranscript}
                          disabled={isSavingTranscript || !pastedTranscript.trim()}
                        >
                          {isSavingTranscript ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Saving...
                            </>
                          ) : (
                            <>
                              <FileText className="w-4 h-4 mr-2" />
                              Save Transcript
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Performance List */}
                  <div>
                    <h3 className="font-display text-sm uppercase tracking-wider mb-4 text-muted-foreground">Past Performances</h3>
                    <ScrollArea className="h-[500px]">
                      <div className="space-y-2 pr-4">
                        {performances.length === 0 ? (
                          <div className="text-center py-12 text-muted-foreground">
                            <BarChart3 className="w-12 h-12 mx-auto mb-4 opacity-50" />
                            <p>No performances logged yet</p>
                            <p className="text-xs mt-2">Upload a recording or paste a transcript to get started</p>
                          </div>
                        ) : (
                          performances.map((perf) => {
                            const meta = parsePerformanceNotes(perf.notes);
                            const FileIcon = perf.file_type === 'video' ? FileVideo : 
                                            perf.file_type === 'text' ? FileText : FileAudio;
                            const isUploaded = perf.file_type !== 'text';
                            return (
                              <div
                                key={perf.transcription_id}
                                className="group relative w-full text-left p-3 bg-muted/50 rounded-sm hover:bg-muted/80 transition-colors"
                              >
                                {/* Delete button - appears on hover, positioned above everything */}
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    handleDeletePerformance(e, perf.transcription_id);
                                  }}
                                  className="absolute right-2 top-2 z-20 p-1.5 rounded-sm bg-destructive text-destructive-foreground opacity-0 group-hover:opacity-100 hover:bg-destructive/80 transition-all"
                                  title="Delete performance"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                                <button
                                  onClick={() => handleSelectPerformance(perf)}
                                  className="w-full text-left"
                                >
                                  <div className="flex items-start gap-3">
                                    <FileIcon className="w-5 h-5 text-primary mt-1" />
                                    <div className="flex-1 min-w-0 pr-8">
                                      <p className="font-medium text-sm truncate">{perf.filename}</p>
                                      <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
                                        {isUploaded ? (
                                          <span>Uploaded {new Date(perf.created_at).toLocaleDateString()}</span>
                                        ) : (
                                          <>
                                            {meta.venue && (
                                              <span className="flex items-center gap-1">
                                                <MapPin className="w-3 h-3" />
                                                {meta.venue}
                                              </span>
                                            )}
                                            <span className="text-primary/70">Pasted</span>
                                            <span>{new Date(perf.created_at).toLocaleDateString()}</span>
                                          </>
                                        )}
                                      </div>
                                    </div>
                                    <ChevronRight className="w-4 h-4 text-muted-foreground" />
                                  </div>
                                </button>
                              </div>
                            );
                          })
                        )}
                      </div>
                    </ScrollArea>
                  </div>
                </div>
              </div>
            </div>
          )}
        </TabsContent>

        {/* ============ PROFILE TAB ============ */}
        <TabsContent value="profile" className="flex-1 mt-0 overflow-auto">
          <ProfileTab user={user} />
        </TabsContent>
      </Tabs>

      {/* BitWorkshop - Full screen editor */}
      {isWorkshopOpen && (
        <BitWorkshop
          bit={editingBit}
          categories={categories}
          onSave={handleSaveBit}
          onAutoSave={handleAutoSaveBit}
          onCreateNewBit={handleCreateNewBit}
          onClose={() => setIsWorkshopOpen(false)}
          onExtractSelection={handleExtractSelection}
          onPromoteToBit={handlePromoteToBit}
          onDuplicate={handleDuplicateBit}
          initialContent={initialContent}
          initialIdea={initialIdea}
        />
      )}

      <CategoryManager
        isOpen={isCategoryManagerOpen}
        onClose={() => setIsCategoryManagerOpen(false)}
        categories={categories}
        onCategoriesChange={setCategories}
      />

      <AIBrainstorm
        isOpen={isAIBrainstormOpen}
        onClose={() => setIsAIBrainstormOpen(false)}
        joke={brainstormBit}
      />

      <ResourceLibrary
        isOpen={isResourceLibraryOpen}
        onClose={() => setIsResourceLibraryOpen(false)}
      />

      <IdeaVault
        isOpen={isIdeaVaultOpen}
        onClose={() => setIsIdeaVaultOpen(false)}
        onNavigateToBit={(bitId) => {
          // Find the bit and open it in workshop
          const bit = bits.find(b => b.bit_id === bitId);
          if (bit) {
            setEditingBit(bit);
            setInitialContent("");
            setInitialIdea("");
            setIsWorkshopOpen(true);
            setActiveTab("write");
          }
        }}
      />

      {/* New Setlist Dialog */}
      <Dialog open={isNewSetlistDialogOpen} onOpenChange={setIsNewSetlistDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Create New Setlist</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Input
              placeholder="e.g., 5-min Open Mic, 15-min Feature"
              value={newSetlistName}
              onChange={(e) => setNewSetlistName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleCreateSetlist()}
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsNewSetlistDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateSetlist} disabled={!newSetlistName.trim()}>
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
